/**
 * ModalRepresentante - Formulario de edición/creación de representantes
 * 
 * Props:
 * - open: boolean que indica si el modal está abierto
 * - representante: objeto con datos del representante (null para crear nuevo)
 * - saving: boolean que indica si se está guardando
 * - onCancel: función para cancelar
 * - onSave: función para guardar (recibe el objeto actualizado)
 */

import React, { useState, useEffect } from "react";
import Modal from "antd/es/modal/Modal";

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder,
  disabled = false,
  onlyDigits = false,
  maxLength,
  inputProps = {},
  error,
}) {
  const handleChange = (e) => {
    let v = e.target.value;
    if (onlyDigits) {
      v = v.replace(/\D/g, "").slice(0, maxLength || Infinity);
    }
    onChange(v);
  };

  const mergedInputProps = { ...inputProps };
  if (maxLength) mergedInputProps.maxLength = maxLength;

  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <input
        type={type}
        value={value ?? ""}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
        required={required}
        {...mergedInputProps}
      />
        {error && (
          <p className="mt-1 text-xs text-rose-600">{error}</p>
        )}
    </label>
  );
}

export function ModalRepresentante({
  open,
  representante,
  saving,
  onCancel,
  onSave,
}) {
  const isEditing = !!representante?.id;
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    id: "",
    nombres: "",
    apellidos: "",
    email: "",
    password: "",
    telefono: "",
    cedula: "",
    fecha_nacimiento: "",
    direccion: "",
    observaciones: "",
  });

  const [errors, setErrors] = useState({});

  // Cargar datos del representante al montar el componente
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Simular pequeña carga para consistencia
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (representante) {
          setFormData({
            id: representante.id || "",
            nombres: representante.nombres || "",
            apellidos: representante.apellidos || "",
            email: representante.email || "",
            password: "", // Nunca cargar password en edición
            telefono: representante.telefono || "",
            cedula: representante.cedula || "",
            fecha_nacimiento: representante.fecha_nacimiento?.slice(0, 10) || "",
            direccion: representante.direccion || "",
            observaciones: representante.observaciones || "",
          });
        } else {
          // Limpiar formulario cuando se abre para crear
          setFormData({
            id: "",
            nombres: "",
            apellidos: "",
            email: "",
            password: "",
            telefono: "",
            cedula: "",
            fecha_nacimiento: "",
            direccion: "",
            observaciones: "",
          });
        }
      } catch (error) {
        console.error("Error cargando datos del representante:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      loadData();
    }
  }, [representante, open]);

  // Limpiar errores cuando se cierra el modal
  useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const email = (formData.email || "").trim();
    const emailRegex = /^[^\s@]+@[^^\s@]+\.[^\s@]+$/;
    const allowedDomains = [
      "gmail.com",
      "hotmail.com",
      "outlook.com",
      "yahoo.com",
      "live.com",
      "icloud.com",
    ];

    const newErrors = {};

    if (!emailRegex.test(email)) {
      newErrors.email = "Ingrese un correo electrónico válido.";
    } else {
      const parts = email.split("@");
      if (parts.length !== 2) {
        newErrors.email = "Ingrese un correo electrónico válido.";
      } else {
        const domain = parts[1].toLowerCase();
        if (!allowedDomains.includes(domain)) {
          newErrors.email =
            "El dominio del correo es incorrecto.";
        }
      }
    }

    if (formData.telefono && formData.telefono.length > 10) {
      newErrors.telefono = "El teléfono debe tener como máximo 10 dígitos.";
    }

    if (formData.cedula && formData.cedula.length > 10) {
      newErrors.cedula = "La cédula debe tener como máximo 10 dígitos.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    onSave(formData);
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={isEditing ? "Editar representante" : "Nuevo representante"}
      footer={null}
      width={700}
    >
      {loading ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-1">
                <div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
                <div className="h-9 w-full bg-slate-100 rounded-xl animate-pulse" />
              </div>
            ))}
          </div>
          <div className="space-y-1">
            <div className="h-3 w-24 bg-slate-200 rounded animate-pulse" />
            <div className="h-20 w-full bg-slate-100 rounded-xl animate-pulse" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <div className="h-9 w-20 bg-slate-100 rounded-xl animate-pulse" />
            <div className="h-9 w-32 bg-slate-200 rounded-xl animate-pulse" />
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="Nombres"
              value={formData.nombres}
              onChange={(v) => setFormData((f) => ({ ...f, nombres: v }))}
              required
              disabled={saving}
              placeholder="Ej: Juan"
            />
            <Field
              label="Apellidos"
              value={formData.apellidos}
              onChange={(v) => setFormData((f) => ({ ...f, apellidos: v }))}
              required
              disabled={saving}
              placeholder="Ej: García López"
            />
            <Field
              label="Email"
              type="email"
              value={formData.email}
              onChange={(v) => {
                setFormData((f) => ({ ...f, email: v }));
                if (v && String(v).trim() !== "") setErrors((s) => ({ ...s, email: undefined }));
              }}
              required
              disabled={saving}
              placeholder="correo@ejemplo.com"
              error={errors.email}
            />
            <Field
              label="Teléfono"
              value={formData.telefono}
              onChange={(v) => {
                setFormData((f) => ({ ...f, telefono: v }));
                if (v && String(v).length > 0) setErrors((s) => ({ ...s, telefono: undefined }));
              }}
              disabled={saving}
              placeholder="Ej: 0987654321"
              onlyDigits
              maxLength={10}
              required
              inputProps={{ inputMode: "numeric", pattern: "\\d*", title: "Sólo dígitos (máx. 10)." }}
              error={errors.telefono}
            />
            <Field
              label="Cédula"
              value={formData.cedula}
              onChange={(v) => {
                setFormData((f) => ({ ...f, cedula: v }));
                if (v && String(v).length > 0) setErrors((s) => ({ ...s, cedula: undefined }));
              }}
              disabled={saving || isEditing}
              placeholder="Ej: 1234567890"
              onlyDigits
              maxLength={10}
              required
              inputProps={{ inputMode: "numeric", pattern: "\\d*", title: "Sólo dígitos (máx. 10)." }}
              error={errors.cedula}
            />
            <Field
              label="Fecha de nacimiento"
              type="date"
              required
              value={formData.fecha_nacimiento}
              onChange={(v) => setFormData((f) => ({ ...f, fecha_nacimiento: v }))}
              disabled={saving || isEditing}
              placeholder="Selecciona una fecha"
            />
            <Field
              label="Dirección"
              required
              value={formData.direccion}
              onChange={(v) => setFormData((f) => ({ ...f, direccion: v }))}
              disabled={saving}
              placeholder="Calle, número, ciudad"
            />
          </div>
          
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Observaciones
            </label>
            <textarea
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
              rows={3}
              value={formData.observaciones}
              onChange={(e) =>
                setFormData((f) => ({ ...f, observaciones: e.target.value }))
              }
              disabled={saving}
              placeholder="Información adicional sobre el representante..."
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-60 transition disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {saving
                ? "Guardando..."
                : isEditing
                ? "Guardar cambios"
                : "Crear representante"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}